import type { ReactNode } from 'react';

/**
 * Premium card surface used across admin. Visual recipe (mirror of web's
 * SpotlightCard base):
 *   - Translucent slate background + backdrop blur (vs hard slate-800 fill)
 *   - Larger rounded corners (rounded-2xl) for premium feel
 *   - Soft border that brightens on hover so users get a "this is alive" cue
 *   - Inset top highlight to mimic light catching the edge
 *
 * Optional title row supports emoji + right-aligned action area.
 *
 * Reach for `<Section spotlight>` when the card is the primary content on
 * the page (eg. dashboard tile) so the mouse-tracking light kicks in. Skip
 * spotlight on dense lists / table containers where the extra paint cost
 * isn't worth it.
 */

import { SpotlightCard } from '@/components/effects/index.js';

interface Props {
  title?: string;
  emoji?: string;
  subtitle?: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
  size?: 'sm' | 'md';
  muted?: boolean;
  /** Add mouse-tracking spotlight highlight. Reserve for hero / feature cards. */
  spotlight?: boolean;
  /** Spotlight tint — passed to SpotlightCard.spotlightColor. */
  spotlightColor?: `rgba(${number}, ${number}, ${number}, ${number})`;
}

// 设计系统材质: 哑光卡体 (mat-matte 自带 border + inset 高光), 取代旧 slate 半玻璃。
const SURFACE = 'mat-matte relative rounded-2xl transition-colors';

const SURFACE_NORMAL = 'hover:border-white/[0.12]';
const SURFACE_MUTED = 'opacity-90 hover:opacity-100';

export function Section({
  title,
  emoji,
  subtitle,
  action,
  className = '',
  children,
  size = 'md',
  muted = false,
  spotlight = false,
  spotlightColor,
}: Props): JSX.Element {
  const pad = size === 'sm' ? 'p-4' : 'p-5 md:p-6';
  const tone = muted ? SURFACE_MUTED : SURFACE_NORMAL;
  const hasHeader = title !== undefined || action !== undefined;

  const inner = (
    <>
      {hasHeader && (
        <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
          <div className="min-w-0">
            {title !== undefined && (
              <h2 className="text-sm font-semibold text-slate-200 flex items-baseline gap-1.5">
                {emoji !== undefined && <span className="text-base">{emoji}</span>}
                <span>{title}</span>
              </h2>
            )}
            {subtitle !== undefined && (
              <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
            )}
          </div>
          {action !== undefined && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
    </>
  );

  if (spotlight) {
    return (
      <SpotlightCard
        className={`${SURFACE} ${tone} ${pad} ${className}`}
        {...(spotlightColor !== undefined ? { spotlightColor } : {})}
      >
        {inner}
      </SpotlightCard>
    );
  }

  return <section className={`${SURFACE} ${tone} ${pad} ${className}`}>{inner}</section>;
}
